import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users as UsersIcon, UserPlus, Pencil, KeyRound, AlertTriangle } from "lucide-react";

const emptyForm = { name: "", mobile: "", role: "driver", pin: "" };

export default function Users() {
  const { user: me, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [myPin, setMyPin] = useState("");
  const [confirmText, setConfirmText] = useState("");

  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || form.mobile.length < 10 || form.pin.length < 4) { toast.error("Fill name, 10-digit mobile, 4-digit PIN"); return; }
    setBusy(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      setAddOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ name: u.name, mobile: u.mobile, role: u.role, pin: "" });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!form.name || form.mobile.length < 10) { toast.error("Fill name and 10-digit mobile"); return; }
    if (form.pin && form.pin.length < 4) { toast.error("PIN must be 4 digits (or leave blank to keep current)"); return; }
    setBusy(true);
    try {
      const payload = { name: form.name, mobile: form.mobile, role: form.role };
      if (form.pin) payload.pin = form.pin;
      await api.put(`/users/${editId}`, payload);
      toast.success("User updated");
      setEditOpen(false); setForm(emptyForm); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const changeMyPin = async () => {
    if (myPin.length < 4) { toast.error("Enter a 4-digit PIN"); return; }
    setBusy(true);
    try {
      await api.put(`/users/${me.id}`, { pin: myPin });
      toast.success("Your PIN was changed");
      setPinOpen(false); setMyPin("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const toggle = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    load();
  };

  const runGoLiveReset = async () => {
    if (confirmText !== "GO-LIVE") { toast.error("Type GO-LIVE to confirm"); return; }
    setBusy(true);
    try {
      await api.post("/admin/go-live-reset", { confirm: "GO-LIVE" });
      toast.success("Set up for go-live. Signing you out — log in as Jaykumar (9880165946 / 1234).");
      setResetOpen(false);
      setTimeout(() => { logout(); navigate("/login", { replace: true }); }, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); setBusy(false); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-head text-2xl font-bold flex items-center gap-2"><UsersIcon className="h-6 w-6 text-primary" /> Users</h1>
          <p className="text-sm text-muted-foreground">Owner, office staff and drivers. They log in with mobile + PIN.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="gap-2" onClick={() => { setMyPin(""); setPinOpen(true); }} data-testid="change-my-pin-button">
            <KeyRound className="h-4 w-4" /> Change my PIN
          </Button>
          <Button onClick={() => { setForm(emptyForm); setAddOpen(true); }} data-testid="user-add-button"><UserPlus className="h-4 w-4 mr-2" />Add user</Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="font-mono">{u.mobile}</TableCell>
                <TableCell className="capitalize">{u.role}</TableCell>
                <TableCell>{u.active ? <Badge className="bg-emerald-100 text-emerald-900 border-emerald-200">Active</Badge> : <Badge variant="outline">Inactive</Badge>}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`user-toggle-${u.id}`}>{u.active ? "Deactivate" : "Activate"}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Go-live danger zone */}
      <Card className="p-5 border-rose-200 bg-rose-50/50" data-testid="go-live-card">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
          <div className="space-y-2">
            <h2 className="text-lg font-semibold text-rose-900">Set up for go-live</h2>
            <p className="text-sm text-rose-800/90 max-w-2xl">
              Use this once when the team is ready to start for real. It clears all demo and practice
              activity (deliveries, sales, loads, returns, cash, HP invoices and cylinder stock) and
              removes the built-in demo logins. Your consumer list, ARB catalogue, prices and any real
              staff logins you created are kept. You will be signed out to log back in as the owner.
            </p>
            <Button variant="destructive" className="gap-2" onClick={() => { setConfirmText(""); setResetOpen(true); }} data-testid="go-live-open-button">
              <AlertTriangle className="h-4 w-4" /> Set up for go-live
            </Button>
          </div>
        </div>
      </Card>

      {/* Add user */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="font-mono" data-testid="user-mobile-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>4-digit PIN</Label><Input value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono" data-testid="user-pin-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={busy} data-testid="user-save-button">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit user */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit user</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name-input" /></div>
            <div><Label>Mobile</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })} className="font-mono" data-testid="edit-mobile-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="edit-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="driver">Driver</SelectItem>
                    <SelectItem value="office">Office</SelectItem>
                    <SelectItem value="owner">Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>New PIN (optional)</Label><Input value={form.pin} placeholder="leave blank to keep" onChange={(e) => setForm({ ...form, pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} className="font-mono" data-testid="edit-pin-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={saveEdit} disabled={busy} data-testid="edit-save-button">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change my PIN */}
      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Change my PIN</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label>New 4-digit PIN</Label>
            <Input value={myPin} onChange={(e) => setMyPin(e.target.value.replace(/\D/g, "").slice(0, 4))} className="font-mono text-lg tracking-widest" data-testid="my-pin-input" />
            <p className="text-xs text-muted-foreground">You'll use this new PIN the next time you sign in.</p>
          </div>
          <DialogFooter><Button onClick={changeMyPin} disabled={busy} data-testid="my-pin-save-button">Update PIN</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Go-live confirm */}
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-rose-900">Confirm go-live setup</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This clears all demo/practice activity and removes the demo logins. This cannot be undone.
              Your consumer list, ARB catalogue and prices are kept. Type <span className="font-mono font-semibold text-foreground">GO-LIVE</span> to confirm.
            </p>
            <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="GO-LIVE" className="font-mono" data-testid="go-live-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={runGoLiveReset} disabled={busy || confirmText !== "GO-LIVE"} data-testid="go-live-confirm-button">Clear & set up</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
